import { AccountEntitlement, Locale } from 'generated/gql';
import type { SidebarItemProps } from '@rulebooks/evidend-titan';
import {
  ActionMenu,
  Avatar,
  BoltIcon,
  BusinessIcon,
  People2Icon,
  ButtonGroup,
  Clickable,
  Drawer,
  // DrawerContainer,
  DrawerContent,
  DrawerList,
  DrawerListItem,
  ExitIcon,
  HelpIcon,
  HomeIcon,
  LightbulbIcon,
  Link,
  ListIcon,
  Loader,
  Logo,
  MenuItem,
  Navigation,
  PeopleIcon,
  PersonIcon,
  SearchBar,
  SearchForm,
  spacing,
  Text,
  useGreaterThan,
  WorldIcon,
} from '@rulebooks/evidend-titan';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { GroupLogo } from 'components/GroupLogo/GroupLogo';
import Onboarding from 'components/Onboarding/Onboarding';
import { getInitials } from 'helpers/initials';
import { localeToIso } from 'helpers/locale';
import { stripFromUrl } from 'helpers/stripFromUrl';
import { useAccount, useCanViewGroups, useEntitlements } from 'hooks/useAccount';
import { useSetLocale } from 'hooks/useSetLocale';
import { useRouter } from 'next/router';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { DrawerContainer } from '../Drawer/DrawerContainer';

interface SidebarNavigationProps {
  children?: React.ReactNode;
  hideOnboarding?: boolean;
  query?: string;
  onLogoClick?: () => void;
}

// interface UserInfo {
//   cn: string;
//   email?: string;
//   givenName?: string;
//   login: string;
//   name: string;
//   sn: string;
//   sub: string;
//   updated_at: number;
// }

const ClickableAvatar = styled(Clickable)`
  display: flex;
  justify-content: flex-end;
`;

const GroupLogoContainer = styled.div`
  margin-top: 0.5rem;
`;

const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing(4)};
`;

const UnauthenticatedSidebarNavigationAvatar = () => {
  const { i18n } = useLingui();
  const { route } = useRouter();
  const isLarge = useGreaterThan('s');

  const setLocale = (locale: Locale) => {
    i18n.activate(localeToIso(locale));
  };

  const isDisplayLogin = !route.includes('login');

  const locales = [
    { value: Locale.Dutch, label: 'Nederlands' },
    { value: Locale.EnglishBritish, label: 'English (UK)' },
  ];

  if (isLarge) {
    return (
      <ButtonGroup spaceBetween={4}>
        <ActionMenu
          label={t`Taal`}
          popoverOptions={{
            strategy: 'fixed',
          }}
          popoverPlacement="bottom-start"
          trigger={
            <Link prefixAdornment={<WorldIcon />}>
              <Trans>Taal</Trans>
            </Link>
          }
        >
          {locales.map(({ label, value }) => (
            <MenuItem
              key={value}
              isActive={i18n.locale === value}
              item={{ label, value }}
              onClick={() => setLocale(value)}
            />
          ))}
        </ActionMenu>

        {isDisplayLogin && (
          <Link prefixAdornment={<PersonIcon />} to="/login">
            <Trans>Inloggen</Trans>
          </Link>
        )}
      </ButtonGroup>
    );
  }

  return (
    <ButtonGroup spaceBetween={4}>
      <ActionMenu
        label={t`Taal`}
        popoverOptions={{
          strategy: 'fixed',
        }}
        popoverPlacement="bottom-start"
        trigger={
          <Clickable aria-label={t`Taal`}>
            <Avatar backgroundColor="grey20" foregroundColor="grey60" size={48}>
              <WorldIcon />
            </Avatar>
          </Clickable>
        }
      >
        {locales.map(({ label, value }) => (
          <MenuItem
            key={value}
            isActive={i18n.locale === value}
            item={{ label, value }}
            onClick={() => setLocale(value)}
          />
        ))}
      </ActionMenu>

      {isDisplayLogin && (
        <Link aria-label={t`Inloggen`} to="/login">
          <Avatar size={48} />
        </Link>
      )}
    </ButtonGroup>
  );
};

const UnauthenticatedSidebarNavigation = ({
  onLogoClick,
}: Pick<SidebarNavigationProps, 'onLogoClick'>) => {
  return (
    <Navigation
      isSticky
      avatar={<UnauthenticatedSidebarNavigationAvatar />}
      brandName=""
      logoComponent={<Logo onClick={onLogoClick} />}
    />
  );
};

export const SidebarNavigation = ({
  children,
  query,
  hideOnboarding,
  onLogoClick,
}: SidebarNavigationProps) => {
  const linguiCtx = useLingui();
  const [isDrawerShown, setIsDrawerShown] = useState<boolean>(false);
  const router = useRouter();
  const { logout, user, isAuthenticated, isLoading } = useAuth0();

  const [, hasSuperAdmin] = useEntitlements([AccountEntitlement.SuperAdmin]);
  const isAnonymousUser = useMemo(() => !user, [user]);
  const account = useAccount();
  const [isSearchShown, setSearchShown] = useState(false);

  // Notaries get "My firm", non-notaries get "My company"; super admins get both.
  const isNotary = !!account?.notary;
  const displayMyFirmTab = isNotary || hasSuperAdmin;
  const displayMyCompanyTab = !!account?.group && !isNotary && !hasSuperAdmin;
  // Only group administrators (and Evidend admins) get "Groups"; see useCanViewGroups.
  const canViewGroups = useCanViewGroups();

  const onLoginClickFn = onLogoClick || (() => router.push('/'));

  // Action to reset the release notes date for preview.
  // const [, setLastSeen] = useCookie('onboarding');
  // const resetReleaseNotes = useCallback(() => {
  //   setLastSeen(formatISO(new Date('2023-01-01')));
  //   window.location.reload();
  // }, [setLastSeen]);

  const initials = useMemo(() => getInitials(user?.name || ''), [user?.name]);

  const handleSubmit = (input: string) => {
    router.push({
      pathname: '/zoeken',
      query: {
        query: input,
      },
    });
  };

  const onClickLogout = useCallback(() => {
    const post_logout_redirect_uri = stripFromUrl(window.location.href, 'magic_link');
    console.log('--post logout redirect uri: ', post_logout_redirect_uri); // todo: remove it after the test on develop
    return logout({
      // logoutParams: { returnTo: post_logout_redirect_uri || window.location.origin + '/login' },
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [logout]);

  const setLocale = useSetLocale();

  const sidebarItems = useMemo(() => {
    const items: SidebarItemProps[] = [
      {
        id: '',
        title: t`Home`,
        icon: HomeIcon,
        to: '/',
      },
      {
        id: 'rechtspersoon',
        title: t`Mijn registers`,
        icon: BusinessIcon,
        to: '/registers',
      },
      {
        id: 'workflow',
        title: t`Workflows`,
        icon: BoltIcon,
        to: '/mutaties/overzicht',
      },
      {
        id: 'support',
        title: t`Support en contact`,
        icon: HelpIcon,
        to: '/support',
      },
    ];

    if (displayMyFirmTab) {
      items.push({
        id: 'firm',
        title: t`My firm`,
        icon: BusinessIcon,
        to: '/my-firm',
      });
    }

    if (canViewGroups) {
      items.push({
        id: 'groups',
        title: t`Groups`,
        icon: PeopleIcon,
        to: '/groups',
      });
    }

    if (displayMyCompanyTab && account?.group) {
      items.push({
        id: 'my-company',
        title: t`My company`,
        icon: People2Icon,
        to: '/my-company',
      });
    }

    if (hasSuperAdmin) {
      items.push({
        id: 'users',
        title: t`Users`,
        icon: PersonIcon,
        to: '/users',
      });
    }

    return items;
  }, [
    account?.group,
    canViewGroups,
    displayMyFirmTab,
    displayMyCompanyTab,
    hasSuperAdmin,
    linguiCtx,
  ]);

  // if (isLoadingUserInfo || isLoadingClaims) {
  if (isLoading) {
    return (
      <div data-test-id="userProfileLoader">
        <Loader height={32} variant="spinner" width="30px" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <UnauthenticatedSidebarNavigation onLogoClick={onLoginClickFn} />;
  }

  return (
    <>
      <Navigation
        isSticky
        avatar={
          <ClickableAvatar onClick={() => setIsDrawerShown(true)}>
            <Avatar size={40}>{isAnonymousUser ? <BusinessIcon /> : initials}</Avatar>
          </ClickableAvatar>
        }
        brandName=""
        isSearchShown={isSearchShown}
        searchComponent={
          <SearchBar
            formElement={
              <SearchForm
                defaultValue={query}
                label={t`Zoeken`}
                placeholder={t`Zoeken naar registers, aandeelhouders of certificaathouders`}
                testId="searchElement"
                onSubmit={handleSubmit}
              />
            }
            isShown={isSearchShown}
            labelCancelButton={t`Annuleren`}
            onToggle={setSearchShown}
          />
        }
        sidebarItems={sidebarItems}
        onToggleSearch={setSearchShown}
        logoComponent={<Logo onClick={onLoginClickFn} />}
      />

      {!hideOnboarding && <Onboarding />}

      <Drawer
        isShown={isDrawerShown}
        position="right"
        width={480}
        onClose={() => setIsDrawerShown(false)}
      >
        <DrawerContainer
          avatarIcon={isAnonymousUser ? <BusinessIcon /> : undefined}
          footer={
            <FooterLinks>
              <Link to="https://www.evidend.nl/algemene-voorwaarden">
                <Trans>Algemene voorwaarden</Trans>
              </Link>

              <Link to="https://www.evidend.nl/verwerkersovereenkomst">
                <Trans>Verwerkersovereenkomst</Trans>
              </Link>

              <Link to="https://www.evidend.nl/privacybeleid">
                <Trans>Privacybeleid</Trans>
              </Link>
            </FooterLinks>
          }
          header={user?.name || <Trans>Gebruiker</Trans>}
          headerMeta={
            <>
              <Text color="grey70" type="labelSmall">
                {user?.email || ''}
              </Text>

              {account?.group?.logo && (
                <GroupLogoContainer>
                  <GroupLogo logo={account.group.logo} maxHeight={40} width={120} />
                </GroupLogoContainer>
              )}

              {account?.group?.name && (
                <Text color="grey70" type="labelSmall">
                  {account.group?.name}
                </Text>
              )}
            </>
          }
          headerVariant="avatar"
          initials={isAnonymousUser ? undefined : initials}
        >
          <DrawerContent>
            <DrawerList variant="compact">
              <DrawerListItem linkTo="/price-list" prefixAdornment={<ListIcon />}>
                <Trans>Prijslijst</Trans>
              </DrawerListItem>

              <DrawerListItem linkTo="/support" prefixAdornment={<HelpIcon />}>
                <Trans>Support en contact</Trans>
              </DrawerListItem>

              {linguiCtx.i18n.locale !== 'en-gb' && (
                <DrawerListItem
                  prefixAdornment={<WorldIcon />}
                  onClick={() => setLocale(Locale.EnglishBritish)}
                >
                  Change language to English (UK)
                </DrawerListItem>
              )}

              {linguiCtx.i18n.locale !== 'nl' && (
                <DrawerListItem
                  prefixAdornment={<WorldIcon />}
                  onClick={() => setLocale(Locale.Dutch)}
                >
                  Wijzig taal naar Nederlands
                </DrawerListItem>
              )}

              {/* Include a debug button to trigger the release notes. */}
              {/*{!config.featureFlags.includes('WITHOUT_DEBUG_RELEASE_NOTES') && (*/}
              {/*  <DrawerListItem prefixAdornment={<SettingsIcon />} onClick={resetReleaseNotes}>*/}
              {/*    <Trans>Laatste release notes opnieuw tonen</Trans>*/}
              {/*  </DrawerListItem>*/}
              {/*)}*/}

              <DrawerListItem linkTo="/wat-is-er-nieuw" prefixAdornment={<LightbulbIcon />}>
                <Trans>Wat is er nieuw?</Trans>
              </DrawerListItem>

              <DrawerListItem prefixAdornment={<ExitIcon />} onClick={onClickLogout}>
                <Trans>Uitloggen</Trans>
              </DrawerListItem>

              {/*todo: double check this is still needed.*/}
              {/*{claims?.amr?.includes('urn:els:auth:ip') && (*/}
              {/*  <DrawerListItem*/}
              {/*    prefixAdornment={<SwapHorizontalIcon />}*/}
              {/*    onClick={() =>*/}
              {/*      loginWithRedirect()*/}
              {/*    }*/}
              {/*  >*/}
              {/*    <Trans>Inloggen op een ander account</Trans>*/}
              {/*  </DrawerListItem>*/}
              {/*)}*/}
            </DrawerList>
          </DrawerContent>
        </DrawerContainer>
      </Drawer>
      {children}
    </>
  );
};
